/** 로그인 후 사용자 활동이 없으면 자동 로그아웃 (ms). 하루 한 번은 다시 로그인하도록 12시간. */
export const AUTH_IDLE_LOGOUT_MS = 12 * 60 * 60 * 1000;

const STORAGE_KEY = 'cp.auth.lastActivityAt';
/** Google OAuth 등 외부 리다이렉트 왕복 중 — 복귀 직후 stale idle 로그아웃 방지 */
const OAUTH_PENDING_KEY = 'cp.auth.oauthPending';
const TOUCH_THROTTLE_MS = 60_000;

/** OAuth로 브라우저를 떠나기 직전에 호출 */
export function markAuthOAuthPending(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(OAUTH_PENDING_KEY, '1');
  } catch {
    // ignore
  }
  // 예전 기기에 남은 lastActivity가 OAuth 복귀 직후 즉시 idle 로그아웃으로 이어지지 않게 비운다.
  clearAuthLastActivity();
}

/** 부트스트랩에서 1회 소비. true면 이번 로드는 OAuth 복귀로 본다. */
export function consumeAuthOAuthPending(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const pending = window.sessionStorage.getItem(OAUTH_PENDING_KEY) === '1';
    if (pending) window.sessionStorage.removeItem(OAUTH_PENDING_KEY);
    return pending;
  } catch {
    return false;
  }
}

export function clearAuthOAuthPending(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(OAUTH_PENDING_KEY);
  } catch {
    // ignore
  }
}

function readRawLastActivityAt(): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

export function writeAuthLastActivityAt(ts: number = Date.now()): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(ts));
  } catch {
    // ignore
  }
}

export function clearAuthLastActivity(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** 세션은 있는데 활동 시각이 없을 때(최초 도입·구버전) — 즉시 로그아웃하지 않고 지금을 기준으로 잡는다 */
export function ensureAuthActivityBaseline(): void {
  if (readRawLastActivityAt() == null) writeAuthLastActivityAt();
}

export function touchAuthActivity(): void {
  writeAuthLastActivityAt();
}

export function isAuthIdleExpired(now: number = Date.now()): boolean {
  const last = readRawLastActivityAt();
  if (last == null) return false;
  return now - last >= AUTH_IDLE_LOGOUT_MS;
}

function msUntilAuthIdleExpiry(now: number = Date.now()): number {
  const last = readRawLastActivityAt();
  if (last == null) return AUTH_IDLE_LOGOUT_MS;
  return Math.max(0, AUTH_IDLE_LOGOUT_MS - (now - last));
}

/**
 * 로그인 상태에서 활동 감지·가시성 복귀 시 만료 검사·타이머 예약.
 * 탭만 다시 보일 때는 활동 시각만 갱신하고(네트워크 없음), 만료된 경우에만 onIdle.
 * 데이터 풀 조회는 하지 않는다.
 */
export function startAuthIdleWatch(onIdle: () => void): () => void {
  let throttleTimer: ReturnType<typeof setTimeout> | null = null;
  let logoutTimer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;

  const scheduleLogout = () => {
    if (logoutTimer) clearTimeout(logoutTimer);
    const ms = msUntilAuthIdleExpiry();
    if (ms <= 0) {
      onIdle();
      return;
    }
    logoutTimer = setTimeout(() => {
      if (!disposed) onIdle();
    }, ms);
  };

  const onActivity = () => {
    if (disposed) return;
    if (throttleTimer) return;
    writeAuthLastActivityAt();
    scheduleLogout();
    throttleTimer = setTimeout(() => {
      throttleTimer = null;
    }, TOUCH_THROTTLE_MS);
  };

  const onVisibility = () => {
    if (disposed || document.visibilityState !== 'visible') return;
    if (isAuthIdleExpired()) {
      onIdle();
      return;
    }
    /** 다른 기기 다녀온 뒤 이 탭으로 복귀 — 조회 없이 idle만 리셋 */
    writeAuthLastActivityAt();
    scheduleLogout();
  };

  const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click'] as const;
  events.forEach((eventName) => window.addEventListener(eventName, onActivity, { passive: true }));
  document.addEventListener('visibilitychange', onVisibility);

  scheduleLogout();

  return () => {
    disposed = true;
    if (logoutTimer) clearTimeout(logoutTimer);
    if (throttleTimer) clearTimeout(throttleTimer);
    events.forEach((eventName) => window.removeEventListener(eventName, onActivity));
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
